import React, { createContext, useContext, useEffect, useState } from 'react';
import { ChatConfigurationSignalingService } from 'views/ChatConfiguration/modules/conversationSignaling/service';
import QueueService from 'views/Report/Callcenter/Autentication/Services/QueueService';

const QueueContentChatContext = createContext();

export const DefaultFilters = {
  FILA: [],
  TAG: [],
  STATUS: [],
  TIME: [],
  START_TYPE: [],
  CHATS_SAVED: false,
  INTERACTION_AGENT_WARNING: false,
  INTERACTION_AGENT_CRITICAL: false,
  INTERACTION_CLIENT: false,
  CHATS_WITH_RESPONSE_AGENT: false
};

const DefaultSort = {
  MORE_TIME_IN_QUEUE: false,
  LAST_MESSAGE_CUSTOMER: false,
  MORE_TIME_IN_WAIT: false,
  CHATS_SAVED: false
};

const getUserSpecificKey = (baseKey, userId) => {
  return `user_${userId}_${baseKey}`;
};

const storageHelper = {
  get: (key, userId) => {
    try {
      const userKey = getUserSpecificKey(key, userId);
      const item = window.localStorage.getItem(userKey);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error(`Erro ao obter ${key} do localStorage:`, error);
      return null;
    }
  },

  set: (key, value, userId) => {
    try {
      const userKey = getUserSpecificKey(key, userId);
      window.localStorage.setItem(userKey, JSON.stringify(value));
    } catch (error) {
      console.error(`Erro ao salvar ${key} no localStorage:`, error);
    }
  }
};

const intervalIds = new Map();

export function QueueContentChatProvider({ children }) {
  const [allChats, setAllChats] = useState([]);
  const [chatsSaved, setChatsSaved] = useState(new Set());
  const [filters, setFilters] = useState(DefaultFilters);
  const [sort, setSort] = useState(DefaultSort);
  const [countChatsInAttendance, setCountChatsInAttendance] = useState(0);
  const [countChatsInWait, setCountChatsInWait] = useState(0);
  const [countAttributedChats, setCountAttributedChats] = useState(0);
  const [queues, setQueues] = useState([]);
  const userId = localStorage.getItem('login');
  const [
    chatsAgentWithoutInteractionCritical,
    setChatsAgentWithoutInteractionCritical
  ] = useState(new Set());
  const [chatsWithResponseAgent, setChatsWithResponseAgent] = useState(
    new Set()
  );
  const [shouldReorganized, setShouldReorganized] = useState(false);
  const [
    chatsAgentWithoutInteractionWarning,
    setChatsAgentWithoutInteractionWarning
  ] = useState(new Set());
  const [
    chatsCustomerWithoutInteractionWarning,
    setChatsCustomerWithoutInteractionWarning
  ] = useState(new Set());
  const [
    chatsCustomerWithoutInteractionCritical,
    setChatsCustomerWithoutInteractionCritical
  ] = useState(new Set());

  useEffect(() => {
    if (!userId) {
      console.error('QueueContentChatProvider sem userLogin');
      return;
    }

    const filtersChats = storageHelper.get('filters-chat-attendance', userId);
    if (filtersChats) {
      setFilters(filtersChats);
    } else {
      storageHelper.set('filters-chat-attendance', DefaultFilters, userId);
    }

    const sortChats = storageHelper.get('sort-chat-attendance', userId);
    if (sortChats) {
      setSort(sortChats);
    } else {
      storageHelper.set('sort-chat-attendance', DefaultSort, userId);
    }

    const savedChats = storageHelper.get('chatsSaved', userId);
    if (savedChats) {
      setChatsSaved(new Set(savedChats));
    }
  }, [userId]);

  useEffect(() => {
    if (!userId || !allChats.length) return;

    const savedChats = storageHelper.get('chatsSaved', userId) || [];
    const validSavedChats = savedChats.filter((chatId) =>
      allChats.some((chat) => chat.id === chatId)
    );

    if (validSavedChats.length !== savedChats.length) {
      storageHelper.set('chatsSaved', validSavedChats, userId);
      setChatsSaved(new Set(validSavedChats));
    }

    const validChatsWithResponseAgent = Array.from(
      chatsWithResponseAgent
    ).filter((chatId) => allChats.some((chat) => chat.id === chatId));

    if (validChatsWithResponseAgent.length !== chatsWithResponseAgent.size) {
      setChatsWithResponseAgent(new Set(validChatsWithResponseAgent));
    }

    const validChatsCustomerWithoutInteractionCritical = Array.from(
      chatsCustomerWithoutInteractionCritical
    ).filter((chatId) => allChats.some((chat) => chat.id === chatId));

    if (
      validChatsCustomerWithoutInteractionCritical.length !==
      chatsCustomerWithoutInteractionCritical.size
    ) {
      setChatsCustomerWithoutInteractionCritical(
        new Set(validChatsCustomerWithoutInteractionCritical)
      );
    }

    const validChatsAgentWithoutInteractionCritical = Array.from(
      chatsAgentWithoutInteractionCritical
    ).filter((chatId) => allChats.some((chat) => chat.id === chatId));

    if (
      validChatsAgentWithoutInteractionCritical.length !==
      chatsAgentWithoutInteractionCritical.size
    ) {
      setChatsAgentWithoutInteractionCritical(
        new Set(validChatsAgentWithoutInteractionCritical)
      );
    }

    const validChatsAgentWithoutInteractionWarning = Array.from(
      chatsAgentWithoutInteractionWarning
    ).filter((chatId) => allChats.some((chat) => chat.id === chatId));

    if (
      validChatsAgentWithoutInteractionWarning.length !==
      chatsAgentWithoutInteractionWarning.size
    ) {
      setChatsAgentWithoutInteractionWarning(
        new Set(validChatsAgentWithoutInteractionWarning)
      );
    }
  }, [allChats, userId]);

  useEffect(() => {
    if (!userId) return;
    storageHelper.set('filters-chat-attendance', filters, userId);
  }, [filters, userId]);

  useEffect(() => {
    if (!userId) return;
    storageHelper.set('sort-chat-attendance', sort, userId);
  }, [sort, userId]);

  const getQueuesChat = async (showIndividualQueue = false) => {
    if (queues.length) {
      if (showIndividualQueue) {
        return queues;
      } else {
        return queues.filter((queue) => queue?.name !== 'Usuario_Especifico');
      }
    }

    const response = await QueueService.getAll(
      undefined,
      undefined,
      false,
      undefined,
      {
        showAllQueues: true
      }
    );

    const result = response.data.data
      .filter((fila) => fila['is_chat'])
      .sort((a, b) => a?.name?.localeCompare(b.name));

    setQueues(result);
    if (showIndividualQueue) return result;
    return result.filter((queue) => queue?.name !== 'Usuario_Especifico');
  };

  const addChatsWithResponseAgent = (chatId) => {
    if (!chatId) return;

    setChatsWithResponseAgent((prev) => prev.add(chatId));
    removeChatsAgentWithoutInteractionCritical(chatId);
    removeChatsAgentWithoutInteractionWarning(chatId);
  };

  const removeChatWithResponseAgent = (chatId) => {
    if (!chatId) return;
    setChatsWithResponseAgent((prev) => {
      const newSet = new Set(prev);
      newSet.delete(chatId);
      return newSet;
    });
  };

  const addChatsAgentWithoutInteractionWarning = (chatId) => {
    if (!chatId) return;

    setChatsAgentWithoutInteractionWarning((prev) => new Set(prev).add(chatId));
    removeChatsAgentWithoutInteractionCritical(chatId);
    removeChatWithResponseAgent(chatId);
  };

  const removeChatsAgentWithoutInteractionWarning = (chatId) => {
    if (!chatId) return;
    setChatsAgentWithoutInteractionWarning((prev) => {
      const newSet = new Set(prev);
      newSet.delete(chatId);
      return newSet;
    });
  };

  const addChatsAgentWithoutInteractionCritical = (chatId) => {
    if (!chatId) return;

    setChatsAgentWithoutInteractionCritical((prev) =>
      new Set(prev).add(chatId)
    );
    removeChatsAgentWithoutInteractionWarning(chatId);
    removeChatWithResponseAgent(chatId);
  };

  const removeChatsAgentWithoutInteractionCritical = (chatId) => {
    if (!chatId) return;
    setChatsAgentWithoutInteractionCritical((prev) => {
      const newSet = new Set(prev);
      newSet.delete(chatId);
      return newSet;
    });
  };

  const addChatsCustomerWithoutInteractionCritical = (chatId) => {
    if (!chatId) return;
    setChatsCustomerWithoutInteractionCritical((prev) =>
      new Set(prev).add(chatId)
    );
    removeChatsCustomerWithoutInteractionWarning(chatId);
  };

  const removeChatsCustomerWithoutInteractionCritical = (chatId) => {
    if (!chatId) return;
    setChatsCustomerWithoutInteractionCritical((prev) => {
      const newSet = new Set(prev);
      newSet.delete(chatId);
      return newSet;
    });
  };

  const addChatsCustomerWithoutInteractionWarning = (chatId) => {
    if (!chatId) return;

    setChatsCustomerWithoutInteractionWarning((prev) =>
      new Set(prev).add(chatId)
    );
    removeChatsCustomerWithoutInteractionCritical(chatId);
  };

  const removeChatsCustomerWithoutInteractionWarning = (chatId) => {
    if (!chatId) return;
    setChatsCustomerWithoutInteractionWarning((prev) => {
      const newSet = new Set(prev);
      newSet.delete(chatId);
      return newSet;
    });
  };

  const getAllChatsSaved = () => {
    if (!userId || !allChats.length) return [];
    const savedChats = storageHelper.get('chatsSaved', userId) || [];
    return savedChats.filter((chatId) =>
      allChats.some((chat) => chat.id === chatId)
    );
  };

  const saveChat = (id) => {
    if (!userId || !id) return;

    const chatExists = allChats.some((chat) => chat.id === id);

    if (!chatExists) {
      return;
    }

    setChatsSaved((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      storageHelper.set('chatsSaved', Array.from(newSet), userId);
      return newSet;
    });
  };

  const checkIfChatIsSaved = (id) => {
    if (!userId || !id || !allChats.length) return false;

    const chatExists = allChats.some((chat) => chat.id === id);
    if (!chatExists) return false;

    const savedChats = getAllChatsSaved();
    return savedChats.includes(id);
  };

  const ActionsConfigMetrics = {
    ADD: (config) => {
      window.localStorage.setItem(
        'configSignalConversation',
        JSON.stringify(config)
      );
    },
    GET: async () => {
      const result = JSON.parse(
        window.localStorage.getItem('configSignalConversation')
      );

      if (!result) {
        const result = await ChatConfigurationSignalingService.get();
        window.localStorage.setItem(
          'configSignalConversation',
          JSON.stringify(result)
        );
        return result;
      }

      return result;
    }
  };
  const getLastMessageCustomer = (
    messages,
    index,
    lastMessage,
    isInitialSequence
  ) => {
    const nextMessage = messages[index];

    if (!nextMessage) return lastMessage;

    if (nextMessage.cliente_ou_atendente === 'cliente') {
      return getLastMessageCustomer(messages, index - 1, nextMessage, false);
    }

    if (nextMessage.cliente_ou_atendente === 'atendente') {
      if (isInitialSequence)
        return getLastMessageCustomer(messages, index - 1, nextMessage, true);
      else return lastMessage;
    }

    return null;
  };

  const getLastMessageAgent = (
    messages,
    index,
    lastMessage,
    isInitialSequence
  ) => {
    const nextMessage = messages[index];

    if (!nextMessage) return lastMessage;

    if (nextMessage.cliente_ou_atendente === 'atendente') {
      return getLastMessageAgent(messages, index - 1, nextMessage, false);
    }

    if (nextMessage.cliente_ou_atendente === 'cliente') {
      if (isInitialSequence)
        return getLastMessageAgent(messages, index - 1, nextMessage, true);
      else return lastMessage;
    }

    return null;
  };

  const getLastMessage = (messages, index) => {
    if (index < 0) return undefined;
    const lastMessage = messages[index];
    if (!lastMessage) return undefined;
    if (lastMessage?.actions?.action === 'default') return lastMessage;
    else return getLastMessage(messages, index - 1);
  };

  const handlerIndividualChatMetrics = async (chat, redistribute) => {
    const chatId = chat?.id || chat?._id;

    const currentTime = new Date().getTime();

    if (intervalIds.has(chatId)) {
      clearInterval(intervalIds.get(chatId));
    }

    const config = await ActionsConfigMetrics.GET();
    if (!config) return;

    if (chat?.mensagens?.length) {
      const lastMessage = getLastMessage(
        chat.mensagens,
        chat.mensagens.length - 1
      );
      if (!lastMessage) return;

      if (!chat.atendente.id_postgres) return;

      if (lastMessage?.cliente_ou_atendente === 'atendente') {
        addChatsWithResponseAgent(chatId);
        removeChatsAgentWithoutInteractionCritical(chatId);
        removeChatsAgentWithoutInteractionWarning(chatId);

        const lastMessageAgent = getLastMessageAgent(
          chat.mensagens,
          chat.mensagens.length - 1,
          null,
          true
        );

        if (!lastMessageAgent) return;

        if (!config?.customerSignaling) return;
        const customerSignalingConfig = config?.customerSignaling.sort(
          (a, b) => b.time - a.time
        );

        if (!customerSignalingConfig) return;

        const timeLastMessageAgent = new Date(
          lastMessageAgent?.hora_da_mensagem
        ).getTime();

        const longerTime = customerSignalingConfig[0]?.time;
        const shorterTime = customerSignalingConfig[1]?.time;

        const existLongerTime = longerTime && longerTime > 0;
        const existShorterTime = shorterTime && shorterTime > 0;

        if (!existLongerTime && !existShorterTime) return;

        let longerTimeExpired = false;
        if (existLongerTime) {
          longerTimeExpired =
            timeLastMessageAgent + longerTime * 1000 < currentTime;
          if (longerTimeExpired) {
            if (redistribute) setShouldReorganized((prev) => !prev);
            if (customerSignalingConfig[0]?.type === 'warning')
              addChatsCustomerWithoutInteractionWarning(chatId);
            else addChatsCustomerWithoutInteractionCritical(chatId);

            return;
          }
          const time = timeLastMessageAgent + longerTime * 1000 - currentTime;

          const idTimeout = setTimeout(() => {
            handlerIndividualChatMetrics(chat, true);
          }, time);

          intervalIds.set(chatId, idTimeout);
        }

        let shorterTimeExpired = false;

        if (existShorterTime) {
          shorterTimeExpired =
            timeLastMessageAgent + shorterTime * 1000 < currentTime;

          if (shorterTimeExpired) {
            if (redistribute) setShouldReorganized((prev) => !prev);
            if (customerSignalingConfig[1].type === 'warning')
              addChatsCustomerWithoutInteractionWarning(chatId);
            else addChatsCustomerWithoutInteractionCritical(chatId);

            return;
          }

          const time = timeLastMessageAgent + shorterTime * 1000 - currentTime;

          const idTimeout = setTimeout(() => {
            handlerIndividualChatMetrics(chat, true);
          }, time);

          intervalIds.set(chatId, idTimeout);
        }
      } else {
        removeChatWithResponseAgent(chatId);
        removeChatsCustomerWithoutInteractionCritical(chatId);
        removeChatsCustomerWithoutInteractionWarning(chatId);

        const lastMessageCustomer = getLastMessageCustomer(
          chat.mensagens,
          chat.mensagens.length - 1,
          null,
          true
        );

        if (!lastMessageCustomer) return;

        const timeLastMessageCustomer = new Date(
          lastMessageCustomer?.hora_da_mensagem
        ).getTime();

        if (!config?.agentSignaling) return;

        const agentSignalingConfig = config?.agentSignaling.sort(
          (a, b) => b.time - a.time
        );

        if (!agentSignalingConfig) return;

        const longerTime = agentSignalingConfig[0]?.time;
        const shorterTime = agentSignalingConfig[1]?.time;

        const existLongerTime = longerTime && longerTime > 0;
        const existShorterTime = shorterTime && shorterTime > 0;

        if (!existLongerTime && !existShorterTime) return;
        let longerTimeExpired = false;
        if (existLongerTime) {
          longerTimeExpired =
            timeLastMessageCustomer + longerTime * 1000 < currentTime;
          if (longerTimeExpired) {
            if (redistribute) setShouldReorganized((prev) => !prev);
            if (agentSignalingConfig[0]?.type === 'warning')
              addChatsAgentWithoutInteractionWarning(chatId);
            else addChatsAgentWithoutInteractionCritical(chatId);

            return;
          }
          const time =
            timeLastMessageCustomer + longerTime * 1000 - currentTime;

          const idTimeout = setTimeout(() => {
            handlerIndividualChatMetrics(chat, true);
          }, time);

          intervalIds.set(chatId, idTimeout);
        }

        let shorterTimeExpired = false;

        if (existShorterTime) {
          shorterTimeExpired =
            timeLastMessageCustomer + shorterTime * 1000 < currentTime;

          if (shorterTimeExpired) {
            if (redistribute) setShouldReorganized((prev) => !prev);
            if (agentSignalingConfig[1].type === 'warning')
              addChatsAgentWithoutInteractionWarning(chatId);
            else addChatsAgentWithoutInteractionCritical(chatId.chats);

            return;
          }

          const time =
            timeLastMessageCustomer + shorterTime * 1000 - currentTime;

          const idTimeout = setTimeout(() => {
            handlerIndividualChatMetrics(chat, true);
          }, time);

          intervalIds.set(chatId, idTimeout);
        }
      }
    }
    return;
  };

  return (
    <QueueContentChatContext.Provider
      userId={userId}
      value={{
        chatsAgentWithoutInteractionCritical,
        chatsAgentWithoutInteractionWarning,
        chatsCustomerWithoutInteractionCritical,
        chatsCustomerWithoutInteractionWarning,
        chatsWithResponseAgent,
        saveChat,
        chatsSaved,
        setChatsSaved,
        checkIfChatIsSaved,
        filters,
        sort,
        setSort,
        setFilters,
        countChatsInAttendance,
        setCountChatsInAttendance,
        countChatsInWait,
        setCountChatsInWait,
        countAttributedChats,
        setCountAttributedChats,
        setAllChats,
        allChats,
        handlerIndividualChatMetrics,
        getQueuesChat,
        shouldReorganized
      }}
    >
      {children}
    </QueueContentChatContext.Provider>
  );
}

/**
 *
 * @typedef {Function} useQueueContentChatContext
 * @returns {{
 *  chatsAgentWithoutInteractionCritical: Set,
 * chatsAgentWithoutInteractionWarning: Set,
 * chatsCustomerWithoutInteractionCritical: Set,
 * chatsCustomerWithoutInteractionWarning: Set,
 * chatsWithResponseAgent: Set,
 * saveChat: Function,
 * chatsSaved: Set,
 * setChatsSaved: Function,
 * checkIfChatIsSaved: Function,
 * filters: Object,
 * sort: Object,
 * setSort: Function,
 * setFilters: Function,
 * countChatsInAttendance: Number,
 * setCountChatsInAttendance: Function,
 * countChatsInWait: Number,
 * setCountChatsInWait: Function,
 * countAttributedChats: Number,
 * setCountAttributedChats: Function,
 * setAllChats: Function,
 * allChats: Array,
 * handlerIndividualChatMetrics: Function
 * getQueuesChat: Function
 * shouldReorganized: Boolean
 * }}
 */
export function useQueueContentChatContext() {
  const context = useContext(QueueContentChatContext);
  if (!context) {
    throw new Error(
      'useQueueContentChatContext must be used within a QueueContentChatProvider'
    );
  }
  return context;
}
